import Form from "./Form";
import LoaderOverlay from "../LoaderOverlay";
import { IncomingPurchaseRequest, IPurchase, postRequest } from "./Data";
import { Toast } from "../../Components/Dialog/Toast";
import Confetti from "../RentalAndAppointments/Confetti";
import { Checkbox } from "../../Components/Form/Checkbox";
import { AbstractFormElement } from "../../Components/Form/Abstract";
import { InputText } from "../../Components/Form/Input";
import { formatThousandSeparator } from "../../Utils/Number";
import { ImageUploader } from "./ImageUploader";
import { TYPES_INCOMING_PURCHASE_REQUEST, SELECTORS_FORM_B2B as SELECTORS } from "./Constants";
import FilePond, { FilePondCustomEvent } from "./FilePond";

export class FormB2B {
    CONFETTI_OPTIONS = {
        count: 200,
        defaults: {
            origin: { y: 0.7 }
        }
    };

    protected readonly form: HTMLFormElement;
    private images: Set<string>;
    private thumbnails: Set<string>;

    private readonly pond: FilePond;

    private readonly confetti = new Confetti(this.CONFETTI_OPTIONS.count, this.CONFETTI_OPTIONS.defaults);
    private submitHandler: () => void;
    private invalidHandler: () => void;

    private readonly imageHandler: (event: FilePondCustomEvent) => void;

    constructor(form: HTMLFormElement) {
        this.form = form;
        this.images = new Set();
        this.thumbnails = new Set();

        if (this.form.querySelector("input.filepond")) {
            this.pond = new FilePond(this.form.querySelector("input.filepond"));
            this.imageHandler = this.handleImages.bind(this);
        }

        this.initHandlers().attachEvents();
    }

    /**
     * initialize handlers
     * @private
     */
    private initHandlers() {
        this.submitHandler = this.handleSubmit.bind(this);
        this.invalidHandler = this.handleInvalid.bind(this);
        return this;
    }

    private attachEvents() {
        this.form.addEventListener("submit", this.submitHandler);
        this.form.addEventListener("invalid", this.invalidHandler);

        for (const element in Array.from(this.form.elements)) {
            const formElement = this.form.elements[element] as AbstractFormElement;

            if (formElement instanceof InputText && formElement.inputMode === "decimal") {
                formElement.addEventListener("input", () => formatThousandSeparator(formElement));
            }
            if (formElement instanceof Checkbox) {
                formElement.addEventListener("change", () => {
                    formElement.value = formElement.checked ? "1" : "0";
                    const formData = new FormData(this.form);
                    formData.set(formElement.name, formElement.value);
                });
            }
            if (formElement instanceof ImageUploader) {
                formElement.addEventListener("change", () => {
                    this.images = JSON.parse(formElement.images);
                    this.thumbnails = JSON.parse(formElement.thumbnails);
                });
            }
        }

        if (this.pond) {
            document.addEventListener("FilePond:addFile", this.imageHandler);
            document.addEventListener("FilePond:deleteFile", this.imageHandler);
            document.addEventListener("FilePond:reset", () => {
                this.images = new Set();
                this.thumbnails = new Set();
            });
        }

        return this;
    }

    async handleSubmit(e: SubmitEvent) {
        e.preventDefault();

        const formData = new FormData(this.form);

        if (this.form.checkValidity()) {
            const loaderOverlay = new LoaderOverlay();

            const state = {
                images: [...this.images],
                thumbnails: [...this.thumbnails],
                request: this.prepareFormData(formData)
            } as IPurchase;

            try {
                loaderOverlay.render();
                const response = await postRequest(state);

                if (response.success) {
                    this.form.reset();

                    new Toast({
                        title: window.SuccessMessages.purchaseRequest.success,
                        type: Toast.TYPE_SUCCESS
                    })?.render();

                    this.confetti.render();
                }
            } catch (e) {
                console.error(e);
                new Toast({
                    title: window.SuccessMessages.purchaseRequest.error,
                    type: Toast.TYPE_ERROR
                })?.render();
            } finally {
                setTimeout(() => {
                    location.reload();
                }, 3000);
            }
        }
    }

    handleInvalid(e: CustomEvent) {
        e.detail.invalidElements[0].scrollIntoView({
            behavior: "smooth",
            block: "center"
        });
        new Toast({
            title: window.SuccessMessages.default.fillInAllMandatoryFields,
            type: Toast.TYPE_ERROR
        })?.render();
    }

    /**
     * prepare form data
     * @param { FormData } formData
     * @return IncomingPurchaseRequest
     */
    prepareFormData(formData: FormData): IncomingPurchaseRequest {
        let data: IncomingPurchaseRequest;
        formData.forEach((value, key: keyof IncomingPurchaseRequest) => {
            if (TYPES_INCOMING_PURCHASE_REQUEST[key] === "number") {
                data = {
                    ...data,
                    [key]: value ? value : null
                };
            } else if (TYPES_INCOMING_PURCHASE_REQUEST[key] === "boolean") {
                data = {
                    ...data,
                    [key]: Boolean(value)
                };
            } else {
                data = {
                    ...data,
                    [key]: value
                };
            }
        });

        if (data.pv) {
            data.pv = String(data.pv).replace(".", "");
        }

        if (data.km) {
            data.km = Number(String(data.km).replace(".", ""));
        }
        if (data.kd_km) {
            data.kd_km = Number(String(data.kd_km).replace(".", ""));
        }

        return Form.prepareRequest(data);
    }

    private handleImages(event: FilePondCustomEvent) {
        const urls = event.detail;
        this.images = urls.images;
        this.thumbnails = urls.thumbnails;
    }
}

export default FormB2B;

const form = document.querySelector(SELECTORS.form) as HTMLFormElement;
if (form) {
    new FormB2B(form);
}
